import { Link } from "react-router-dom";
import { Facebook, Twitter, Instagram, Linkedin, Youtube } from "lucide-react";
import { Icon } from "@iconify/react";
import { otherLinks } from "@/constants";
import { useAuthorRedirect } from "@/hooks/useAuthorRedirect";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { useIntercom } from "@/hooks/useIntercom";

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const { goToAuthorPlatform } = useAuthorRedirect();
  const { toast } = useToast();
  const { openMessenger } = useIntercom();

  const handleComingSoon = () => {
    toast({
      title: t('common.comingSoon', "Coming Soon"),
      description: t('common.featureUnderDevelopment', "This feature is currently under development."),
      duration: 3000,
    });
  };

  return (
    <footer className="bg-black border-t border-white/20">
      <div className="container mx-auto py-16 px-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-12">
          <div className="space-y-4">
            <h3 className="text-2xl font-bold">
              <span className="text-anzir">A</span>nzir
            </h3>
            <p className="text-white text-[14px] max-w-xs">
              {t('footer.description', 'A global MysteryAct platform where technology meets artistry, creating immersive narrative experiences.')}
            </p>
          </div>

          <div>
            <h4 className="font-medium text-[18px] mb-4 text-white">
              <span>{t('footer.forPlayers', 'For Players')}</span>
            </h4>
            <ul className="space-y-4">
              <li>
                <Link to="/online" className="text-white text-[14px] hover:text-gray-400 transition-colors">
                  {t('nav.online', 'Online MysteryAct')}
                </Link>
              </li>
              <li>
                <Link to="/offline" className="text-white text-[14px] hover:text-gray-400 transition-colors">
                  {t('nav.offline', 'Offline MysteryAct')}
                </Link>
              </li>
              <li>
                <Link to="/library" className="text-white text-[14px] hover:text-gray-400 transition-colors">
                  {t('footer.browseStories', 'Browse Stories')}
                </Link>
              </li>
              <li>
                <Link to="/what-is-mysteryact" className="text-white text-[14px] hover:text-gray-400 transition-colors">
                  {t('nav.whatismysteryact', 'What Is MysteryAct')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-[18px] mb-4 text-white">
              <span>{t('footer.forAuthors', 'For Authors')}</span>
            </h4>
            <ul className="space-y-4">
              {/* <li>
                <Link to="/author" className="text-white text-[14px] hover:text-gray-400 transition-colors">
                  {t('nav.becomeanauthor', 'Become An Author')}
                </Link>
              </li> */}
              <li>
                <button
                  onClick={goToAuthorPlatform}
                  className="text-white text-[14px] hover:text-gray-400 transition-colors text-left"
                >
                  {t('nav.creationplatform', 'Creation Platform')}
                </button>
              </li>
              {/* <li>
                <Link to="/help" className="text-white text-[14px] hover:text-gray-400 transition-colors">
                  {t('footer.storytellingGuidelines', 'Storytelling Guidelines')}
                </Link>
              </li> */}
              <li>
                <a href="https://intercom.help/anzir/en/collections/12088635-author" className="text-white text-[14px] hover:text-gray-400 transition-colors">
                  {t('footer.authorResources', 'Author Resources')}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-[18px] mb-4 text-white">
              <span>{t('footer.forStoreOwners', 'For Store Owners')}</span>
            </h4>
            <ul className="space-y-4">
              <li>
                <Link to="/store" className="text-white text-[14px] hover:text-gray-400 transition-colors">
                  {t('nav.becomeastoreowner', 'Become A Store Owner')}
                </Link>
              </li>
              <li>
                <Link to="/stores" className="text-white text-[14px] hover:text-gray-400 transition-colors">
                  {t('footer.findStores', 'Find Stores')}
                </Link>
              </li>
              <li>
                <a  href="https://intercom.help/anzir/en/collections/12231264-local-venue-online-studio-partner" target="_blank" className="text-white text-[14px] hover:text-gray-400 transition-colors">
                  {t('footer.storeResources', 'Store Resources')}
                </a>
              </li>
              <li>
                <a href="https://intercom.help/anzir/en/collections/12231264-local-venue-online-studio-partner" target="_blank" rel="noopener noreferrer" className="text-white text-[14px] hover:text-gray-400 transition-colors">
                  {t('footer.hostGuidelines', 'Host Guidelines')}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-[18px] mb-4 text-white">
              <span>{t('footer.other', 'Other')}</span>
            </h4>
            <ul className="space-y-4">
              {otherLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.path} className="text-white text-[14px] hover:text-gray-400 transition-colors">
                    {t(`nav.${link.name.toLowerCase()}`, link.name)}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/help" className="text-white text-[14px] hover:text-gray-400 transition-colors">
                  {t('nav.help', 'Help Center')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6">
          <div className="flex space-x-6">
            <a href="https://www.linkedin.com/company/anzir" target="_blank" rel="noopener noreferrer" className="text-white hover:text-anzir transition-colors">
              <Linkedin className="w-6 h-6" />
            </a>
            <a href="https://twitter.com/anzir_office" target="_blank" rel="noopener noreferrer" className="text-white hover:text-anzir transition-colors">
              <Icon icon="fa6-brands:x-twitter" className="w-6 h-6" />
            </a>
            <a href="https://www.instagram.com/anzir_offical" target="_blank" rel="noopener noreferrer" className="text-white hover:text-anzir transition-colors">
              <Instagram className="w-6 h-6" />
            </a>
            <a href="https://www.youtube.com/channel/UCkq-qnPq5ijc_x0iW7OETEQ" target="_blank" rel="noopener noreferrer" className="text-white hover:text-anzir transition-colors">
              <Youtube className="w-6 h-6" />
            </a>
            <a href="https://discord.com/invite/qr3HPC5AkH" target="_blank" rel="noopener noreferrer" className="text-white hover:text-anzir transition-colors">
              <Icon icon="ic:baseline-discord" className="w-6 h-6" />
            </a>
          </div>
        </div>

        <div className="mt-8 flex flex-col md:flex-row justify-between items-center">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <p className="text-white text-xs">
              © {currentYear} Anzir. {t('footer.slogan', 'Where technology meets narrative art.')}
            </p>
            <a href="https://www.nvidia.com/en-us/startups/" target="_blank" rel="noopener noreferrer" className="bg-white p-1 rounded inline-flex items-center">
              <img
                src="/lovable-uploads/0115e22e-5789-42d2-b7bf-912402a9dc05.webp"
                alt={t('footer.nvidiaAlt', 'NVIDIA Inception Program')}
                className="h-8"
              />
            </a>
          </div>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/agreement/player" className="text-white text-xs font-light hover:text-gray-400 transition-colors">
              {t('footer.userPolicy', 'User Policy')}
            </Link>
            <Link to="/agreement/privacy-policy" className="text-white text-xs font-light hover:text-gray-400 transition-colors">
              {t('footer.privacy', 'Privacy')}
            </Link>
            <button
              onClick={openMessenger}
              className="text-white text-xs font-light hover:text-gray-400 transition-colors"
            >
              {t('footer.contact', 'Contact')}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
